import * as React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, CheckCircle2, ChevronRight,
  FileText, Mic, Shield, User,
} from 'lucide-react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { VoiceAgent } from '@xyqar/shared-voice';
import { uploadOnboardingAsset } from '../components/wizard/fileAssets';
import { LiveVideoCapture } from '../components/LiveVideoCapture';
import { AUTH_RETURN_TO_KEY } from '../authReturnTo';
import type { UploadedAsset } from '../components/wizard/fileAssets';

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE ?? 'https://api.xyqar.com';
const GENESIS_URL =
  (import.meta as any).env?.VITE_GENESIS_URL ?? 'https://genesis.xyqar.com';

function buildGenesisReturnUrl() {
  const url = new URL(GENESIS_URL);
  url.searchParams.set('return_to', window.location.href);
  return url.toString();
}

type Role = 'cto' | 'gtm';
type Step = 'consent' | 'background' | 'interview';

const ROLE_META: Record<Role, {
  title: string;
  subtitle: string;
  comp: string;
  description: string;
  ariaAgentNote: string;
}> = {
  cto: {
    title: 'Chief Technology Officer',
    subtitle: 'Engineering · Architecture · Security · Zero-trust',
    comp: 'Equity-first — cash unlocks at Series A',
    description:
      'ARIA will conduct a technical interview. She will ask you about your architecture decisions, security approach, and leadership philosophy. Be direct and specific — ARIA evaluates depth, not breadth.',
    ariaAgentNote: 'ARIA posera 7 questions techniques en français sur votre expérience CTO, vos choix d\'architecture, votre vision sécurité et votre capacité à construire une équipe 0→1.',
  },
  gtm: {
    title: 'Go-To-Market Lead',
    subtitle: 'Enterprise Sales · B2B · 100% commission',
    comp: '100% commission · uncapped + equity options',
    description:
      'ARIA will conduct a sales and GTM interview. She will ask about your pipeline methodology, largest deals closed, network access, and how you handle zero-support early-stage selling.',
    ariaAgentNote: 'ARIA posera 7 questions GTM en français sur votre expérience enterprise sales, vos méthodes de prospection, vos deals signés et votre approche commission-only.',
  },
};

interface S {
  step: Step;
  consentData: boolean;
  consentRecord: boolean;
  consentCCPA: boolean;
  bio: string;
  photoAsset: UploadedAsset | null;
  cvAssetId: string | null;
  transcriptId: string | null;
  scoreDelta: number | null;
  videoAssetId: string | null;
  authToken: string;
}

const init = (): S => ({
  step: 'consent',
  consentData: false, consentRecord: false, consentCCPA: false,
  bio: '', photoAsset: null, cvAssetId: null,
  transcriptId: null, scoreDelta: null, videoAssetId: null, authToken: '',
});

function InternalHirePage({ role }: { role: Role }) {
  const meta = ROLE_META[role];
  const { principal, loading, getToken } = useXyqarAuth() as any;
  const [s, setS] = React.useState<S>(init);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const photoRef = React.useRef<HTMLInputElement>(null);
  const cvRef = React.useRef<HTMLInputElement>(null);

  const upd = (k: keyof S, v: any) => setS(x => ({ ...x, [k]: v }));

  React.useEffect(() => {
    if (!loading && !principal) {
      sessionStorage.setItem(AUTH_RETURN_TO_KEY, window.location.pathname);
      window.location.replace(buildGenesisReturnUrl());
    }
  }, [loading, principal]);

  React.useEffect(() => {
    if (!principal) return;
    getToken().then((t: string) => upd('authToken', t)).catch(() => {});
  }, [principal, getToken]);

  const steps: Step[] = ['consent', 'background', 'interview'];
  const stepIdx = steps.indexOf(s.step);

  const canConsent = s.consentData && s.consentRecord && s.consentCCPA;
  const canBg      = s.bio.trim().length >= 20;

  const handlePhoto = async (file: File) => {
    if (!s.authToken) return;
    try {
      const up = await uploadOnboardingAsset({
        apiBase: API_BASE, token: s.authToken,
        wizard: role as any, step: 'background', fieldName: 'photo', file,
      });
      upd('photoAsset', up);
    } catch (e: any) { setErr(String(e.message ?? e)); }
  };

  const handleCV = async (file: File) => {
    if (!s.authToken) return;
    try {
      const up = await uploadOnboardingAsset({
        apiBase: API_BASE, token: s.authToken,
        wizard: role as any, step: 'background', fieldName: 'cv', file,
      });
      upd('cvAssetId', up.asset_id);
    } catch (e: any) { setErr(String(e.message ?? e)); }
  };

  const handleVideoRecording = async ({ blob }: { blob: Blob; durationMs: number }) => {
    if (!s.authToken) return;
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `interview_${role}_${Date.now()}.${ext}`, { type: blob.type });
      const up = await uploadOnboardingAsset({
        apiBase: API_BASE, token: s.authToken,
        wizard: role as any, step: 'interview', fieldName: 'video', file,
      });
      upd('videoAssetId', up.asset_id);
    } catch { /* best-effort */ }
  };

  const handleSubmit = async () => {
    if (!s.transcriptId || !principal) return;
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/api/v1/onboarding/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          wizard: role,
          email: principal.email,
          payload: {
            role,
            consent: { data: true, recording: true, ccpa: true },
            background: {
              bio: s.bio,
              photo_asset_id: s.photoAsset?.asset_id ?? null,
              cv_asset_id: s.cvAssetId,
            },
            interview: {
              transcript_id: s.transcriptId,
              score_delta: s.scoreDelta,
              video_asset_id: s.videoAssetId,
            },
          },
        }),
      });
      setSubmitted(true);
    } catch (e: any) { setErr(String(e.message ?? e)); }
    finally { setSubmitting(false); }
  };

  if (loading || !principal) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6 text-center">
      <CheckCircle2 className="h-14 w-14 text-emerald-400" strokeWidth={1.5} />
      <h1 className="text-3xl font-bold">Application received.</h1>
      <p className="text-white/55 max-w-md text-sm">
        The founding team reviews {meta.title} applications personally. Expect a direct reply within 48–72 hours.
      </p>
      <a href="https://xyqar.com/hiring" className="mt-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/60 hover:border-white/40 transition">← All openings</a>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="px-6 py-5 border-b border-white/8 flex items-center justify-between">
        <a href="https://xyqar.com/hiring" className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition">
          <ArrowLeft className="h-4 w-4" />All openings
        </a>
        <div className="flex items-center gap-1.5">
          {steps.map((x, i) => (
            <div key={x} className={`h-1.5 rounded-full transition-all ${i <= stepIdx ? 'w-8 bg-white' : 'w-3 bg-white/20'}`} />
          ))}
        </div>
        <p className="text-[10px] uppercase tracking-widest text-white/30 font-mono">{role.toUpperCase()}</p>
      </header>

      <main className="max-w-xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: CONSENT ─────────────────────────────────────────── */}
          {s.step === 'consent' && (
            <motion.div key="c" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">1 / 3</p>
                  <h1 className="text-xl font-bold">Consent &amp; data</h1>
                </div>
              </div>

              <div className="mb-5 p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
                <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-2">{meta.subtitle}</p>
                <p className="text-base font-semibold">{meta.title}</p>
                <p className="text-xs text-amber-400/80 mt-1 font-mono">{meta.comp}</p>
                <p className="text-sm text-white/50 mt-3 leading-relaxed">{meta.description}</p>
              </div>

              <div className="space-y-3">
                {[
                  {
                    key: 'consentData' as keyof S,
                    title: 'Data collection & storage',
                    body: 'Bluepinksecurity Inc. (Delaware, USA) will store your profile, CV, interview answers and transcript for recruitment purposes. You can request deletion at privacy@bluepinksecurity.com.',
                  },
                  {
                    key: 'consentRecord' as keyof S,
                    title: 'Live recording & AI analysis',
                    body: 'Your video session will be recorded. ARIA (AI) will analyze the conversation. Only the founding team accesses the recording and transcript.',
                  },
                  {
                    key: 'consentCCPA' as keyof S,
                    title: 'CCPA — California privacy rights',
                    body: 'Under CCPA you have the right to know, delete, and opt out of sale of your personal data. We do not sell personal data. Full policy: bluepinksecurity.com/privacy',
                  },
                ].map(({ key, title, body }) => (
                  <label key={key} className="flex gap-4 p-5 rounded-2xl border border-white/10 bg-white/[0.025] cursor-pointer hover:border-white/20 transition">
                    <input
                      type="checkbox"
                      checked={s[key] as boolean}
                      onChange={e => upd(key, e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-white flex-shrink-0"
                    />
                    <div>
                      <p className="font-semibold text-sm">{title}</p>
                      <p className="text-xs text-white/45 mt-1 leading-relaxed">{body}</p>
                    </div>
                  </label>
                ))}
              </div>

              <button
                onClick={() => canConsent && upd('step', 'background')}
                disabled={!canConsent}
                className="mt-7 w-full flex items-center justify-center gap-2 bg-white text-black rounded-full py-4 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
              >
                I agree <ChevronRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {/* ── STEP 2: BACKGROUND ──────────────────────────────────────── */}
          {s.step === 'background' && (
            <motion.div key="b" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <User className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">2 / 3</p>
                  <h1 className="text-xl font-bold">Your background</h1>
                </div>
              </div>

              <div className="space-y-5">
                {/* Photo upload */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-3">
                    Profile photo <span className="normal-case tracking-normal text-white/25">(optional)</span>
                  </p>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handlePhoto(f); }} />
                  <button type="button" onClick={() => photoRef.current?.click()}
                    className="flex items-center gap-2.5 px-5 py-3 rounded-2xl border border-white/15 text-sm text-white/65 hover:border-white/28 transition">
                    <User className="h-4 w-4" strokeWidth={1.8} />
                    {s.photoAsset ? '✓ Photo uploaded — change' : 'Upload profile photo'}
                  </button>
                </div>

                {/* CV upload — goes through virus scanner server-side */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-3">
                    CV / Résumé <span className="normal-case tracking-normal text-white/25">(recommended · PDF or Word)</span>
                  </p>
                  <input ref={cvRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleCV(f); }} />
                  <button type="button" onClick={() => cvRef.current?.click()}
                    className="flex items-center gap-2.5 px-5 py-3 rounded-2xl border border-white/15 text-sm text-white/65 hover:border-white/28 transition">
                    <FileText className="h-4 w-4" strokeWidth={1.8} />
                    {s.cvAssetId ? '✓ CV uploaded — change' : 'Upload CV / résumé'}
                  </button>
                  <p className="mt-2 text-[10px] text-white/28 leading-relaxed">
                    Files are scanned for malware before storage. Max 25 MB. PDF, DOC, DOCX only.
                  </p>
                </div>

                {/* Short bio */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-3">
                    One-paragraph pitch <span className="normal-case tracking-normal text-white/25">(min 20 chars)</span>
                  </p>
                  <textarea
                    value={s.bio}
                    onChange={e => upd('bio', e.target.value)}
                    rows={4}
                    maxLength={800}
                    placeholder={role === 'cto'
                      ? 'e.g. 12 years engineering, last 4 as CTO at [Company]. Built the platform from 0 to 300k users on AWS. Zero-trust architecture, Kubernetes, FastAPI, Terraform. Led 18-person team...'
                      : 'e.g. 8 years enterprise SaaS sales. Closed $12M ARR in year one at [Company]. Strong network in CISO and HR buyer personas across US and EU markets...'}
                    className="w-full rounded-2xl bg-black/40 border border-white/12 px-4 py-4 text-sm text-white placeholder:text-white/22 focus:outline-none focus:border-white/28 resize-none"
                  />
                  <p className="text-right text-[10px] text-white/22 mt-1">{s.bio.trim().length}/800</p>
                </div>
              </div>

              {err && <p className="mt-4 text-sm text-rose-400 rounded-xl border border-rose-500/25 bg-rose-500/7 px-4 py-3">{err}</p>}

              <div className="flex gap-3 mt-7">
                <button onClick={() => upd('step', 'consent')} className="px-5 py-3 rounded-full border border-white/12 text-sm text-white/55 hover:border-white/25 transition">Back</button>
                <button
                  onClick={() => canBg && upd('step', 'interview')}
                  disabled={!canBg}
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
                >
                  Start ARIA interview <Mic className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: ARIA INTERVIEW ───────────────────────────────────── */}
          {s.step === 'interview' && (
            <motion.div key="i" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <Mic className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">3 / 3 · Live interview</p>
                  <h1 className="text-xl font-bold">ARIA is waiting</h1>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 mb-5">
                <p className="text-sm text-white/60 leading-relaxed">{meta.ariaAgentNote}</p>
                <p className="text-xs text-white/35 mt-3">
                  1. Enable your camera. 2. Click <strong className="text-white">"Start a call"</strong>. 3. Answer naturally. End when ARIA concludes.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 mb-5">
                <LiveVideoCapture
                  apiBase={API_BASE}
                  authToken={s.authToken || null}
                  onRecordingReady={handleVideoRecording}
                  onError={msg => setErr(msg)}
                />

                <div className="rounded-2xl overflow-hidden border border-white/10">
                  {s.authToken ? (
                    <VoiceAgent
                      apiBase={API_BASE}
                      authToken={s.authToken}
                      persona={role as any}
                      metadata={{ role }}
                      allowTextFallback
                      height={440}
                      className="rounded-2xl"
                      language="fr"
                      onTranscriptSaved={({ id, score_delta }: { id: string; score_delta: number }) => {
                        upd('transcriptId', id);
                        upd('scoreDelta', score_delta);
                      }}
                    />
                  ) : (
                    <div className="h-40 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white/18 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {s.transcriptId ? (
                <div className="flex items-center gap-3 mb-5 p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/6">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" strokeWidth={1.8} />
                  <p className="text-sm text-emerald-300">Interview transcript saved. You can now submit your application.</p>
                </div>
              ) : (
                <p className="text-sm text-white/38 mb-5">Complete the ARIA conversation to enable submit.</p>
              )}

              {err && <p className="mb-4 text-sm text-rose-400 rounded-xl border border-rose-500/25 bg-rose-500/7 px-4 py-3">{err}</p>}

              <div className="flex gap-3">
                <button onClick={() => upd('step', 'background')} disabled={submitting} className="px-5 py-3 rounded-full border border-white/12 text-sm text-white/55 hover:border-white/25 transition disabled:opacity-35">Back</button>
                <button
                  onClick={handleSubmit}
                  disabled={!s.transcriptId || submitting}
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
                >
                  {submitting ? 'Submitting…' : 'Submit application'}
                  {!submitting && <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}

export function CTOApplicationPage() { return <InternalHirePage role="cto" />; }
export function GTMApplicationPage()  { return <InternalHirePage role="gtm" />; }
